import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { COMPONENT_TYPES, DEFAULTS } from "@/components/builder/BlockRenderer";
import { TemplatePicker } from "@/components/TemplatePicker";
import RichTextEditor from "@/components/RichTextEditor";
import {
  ArrowLeft, Save, Trash2, Type, Image as ImageIcon, MousePointer, Layers,
  Star, HelpCircle, Minus, FormInput, Plus, Upload, GripVertical, Eye,
  Monitor, Tablet, Smartphone, CalendarDays, Mail, Tag,
} from "lucide-react";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const ICONS = {
  heading: Type, text: Type, image: ImageIcon, button: MousePointer,
  hero: Layers, slider: Layers, testimonials: Star, faq: HelpCircle, spacer: Minus,
  form: FormInput, booking: CalendarDays, newsletter: Mail, team: Layers, image_fullwidth: ImageIcon, loyalty: Star, pricelist: Tag,
};

const DEVICES = {
  desktop: { label: "Desktop", icon: Monitor, width: "100%" },
  tablet: { label: "Tablet", icon: Tablet, width: "820px" },
  mobile: { label: "Mobile", icon: Smartphone, width: "390px" },
};

function newBlockId() { return Math.random().toString(36).slice(2, 11); }

export default function PageBuilder() {
  const { id } = useParams();
  const { salon } = useAuth();
  const [page, setPage] = useState(null);
  const [selected, setSelected] = useState(null);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [theme, setTheme] = useState({});
  const [forms, setForms] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [addons, setAddons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [header, setHeader] = useState({});
  const [footer, setFooter] = useState({});
  const [loyalty, setLoyalty] = useState(null);
  const [saving, setSaving] = useState(false);
  const [device, setDevice] = useState("desktop");
  const iframeRef = useRef(null);
  const [iframeReady, setIframeReady] = useState(false);

  const load = useCallback(async () => {
    const [p, t, f, s, h, ft, st] = await Promise.all([
      api.get(`/cms/pages/${id}`),
      api.get(`/cms/theme`),
      api.get(`/cms/forms`),
      api.get(`/cms/services`),
      api.get(`/cms/header`),
      api.get(`/cms/footer`),
      api.get(`/cms/staff`),
    ]);
    setPage(p.data); setTheme(t.data || {}); setForms(f.data || []);
    setServices(s.data || []); setHeader(h.data || {}); setFooter(ft.data || {});
    setStaff(st.data || []);
    // Addons + categories: best-effort (some plans may not have categories feature)
    try { const { data } = await api.get(`/cms/addons`); setAddons(data || []); } catch { setAddons([]); }
    try { const { data } = await api.get(`/cms/service-categories`); setCategories(data || []); } catch { setCategories([]); }
    // Optional loyalty data (gracefully ignore if user has no access)
    try {
      const [ls, lr] = await Promise.all([
        api.get(`/cms/loyalty/settings`),
        api.get(`/cms/loyalty/rewards`),
      ]);
      if (!ls.data || !lr.data) {
        setLoyalty(null);
      } else {
        setLoyalty({ settings: ls.data, rewards: (lr.data || []).filter((r) => r.active) });
      }
    } catch {
      setLoyalty(null);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Listen for click events from iframe
  useEffect(() => {
    const onMsg = (e) => {
      if (!e.data || e.data.__mv !== "preview") return;
      if (e.data.type === "click") {
        setSelected(e.data.id);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // Post state to iframe whenever inputs change
  const postState = useCallback(() => {
    if (!iframeRef.current?.contentWindow || !page) return;
    iframeRef.current.contentWindow.postMessage({
      __mv: "preview",
      type: "state",
      payload: {
        salon, theme, header, footer, forms, services, staff, loyalty, addons, categories,
        blocks: page.blocks || [],
      },
    }, "*");
  }, [page, theme, header, footer, forms, services, staff, salon, loyalty, addons, categories]);

  useEffect(() => { if (iframeReady) postState(); }, [iframeReady, postState]);

  const onIframeLoad = () => setIframeReady(true);

  // Sync selection to iframe
  useEffect(() => {
    if (!iframeRef.current?.contentWindow) return;
    iframeRef.current.contentWindow.postMessage({ __mv: "preview", type: "select", id: selected }, "*");
  }, [selected]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  if (!page) return <div className="p-8">Lade...</div>;

  const update = (patch) => setPage((p) => ({ ...p, ...patch }));
  const updateBlocks = (blocks) => setPage((p) => ({ ...p, blocks }));
  const insertTemplate = (newBlocks) => updateBlocks([...(page?.blocks || []), ...newBlocks]);

  const addBlock = (type) => {
    const b = { id: newBlockId(), type, props: { ...DEFAULTS[type] } };
    updateBlocks([...(page.blocks || []), b]);
    setSelected(b.id);
  };

  const removeBlock = (bid) => {
    updateBlocks(page.blocks.filter((b) => b.id !== bid));
    if (selected === bid) setSelected(null);
  };

  const updateBlockProps = (bid, props) => {
    updateBlocks(page.blocks.map((b) => (b.id === bid ? { ...b, props: { ...b.props, ...props } } : b)));
  };

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = page.blocks.findIndex((b) => b.id === active.id);
    const newIdx = page.blocks.findIndex((b) => b.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    updateBlocks(arrayMove(page.blocks, oldIdx, newIdx));
  };

  const onCanvasDragOver = (e) => e.preventDefault();
  const onCanvasDrop = (e) => {
    e.preventDefault();
    const data = e.dataTransfer.getData("text/plain");
    if (data.startsWith("new:")) addBlock(data.slice(4));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/cms/pages/${id}`, {
        title: page.title, slug: page.slug, blocks: page.blocks,
        seo: page.seo || {}, is_home: !!page.is_home, published: !!page.published,
      });
    } finally { setSaving(false); }
  };

  const selectedBlock = page.blocks.find((b) => b.id === selected);
  const previewUrl = `${window.location.origin}/__preview__`;

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: "var(--mv-canvas)" }}>
      {/* Left: Components + Outline */}
      <aside className="w-64 border-r flex-shrink-0 flex flex-col" style={{ background: "#fff", borderColor: "var(--mv-border)" }}>
        <div className="p-4 border-b" style={{ borderColor: "var(--mv-border)" }}>
          <Link to="/cms/pages" className="text-xs flex items-center gap-1.5 mb-3" style={{ color: "var(--mv-text-muted)" }}>
            <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Zurück
          </Link>
          <input
            data-testid="page-title-edit"
            className="font-display text-base bg-transparent border-0 focus:outline-none w-full"
            value={page.title}
            onChange={(e) => update({ title: e.target.value })}
          />
          <p className="text-xs" style={{ color: "var(--mv-text-muted)" }}>/{page.slug}</p>
        </div>

        <div className="p-3 border-b" style={{ borderColor: "var(--mv-border)" }}>
          <button
            data-testid="open-template-picker"
            type="button"
            onClick={() => setTemplateOpen(true)}
            className="mv-btn text-xs w-full justify-center"
          >
            Vorlagen einfügen
          </button>
        </div>

        <div className="p-3 border-b" style={{ borderColor: "var(--mv-border)" }}>
          <p className="mv-label mb-2">Komponenten</p>
          <div className="grid grid-cols-2 gap-1.5">
            {COMPONENT_TYPES.map((c) => {
              const Ico = ICONS[c.type] || Layers;
              return (
                <button
                  key={c.type}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData("text/plain", `new:${c.type}`)}
                  onClick={() => addBlock(c.type)}
                  data-testid={`add-component-${c.type}`}
                  className="flex flex-col items-center gap-1 p-2.5 rounded-lg border text-[10px] hover:bg-[var(--mv-surface)] cursor-grab active:cursor-grabbing transition-colors"
                  style={{ borderColor: "var(--mv-border)" }}
                >
                  <Ico className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          <p className="mv-label mb-2">Aufbau</p>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={page.blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1">
                {page.blocks.map((b) => (
                  <SortableOutlineItem key={b.id} block={b} selected={selected === b.id} onSelect={() => setSelected(b.id)} onRemove={() => removeBlock(b.id)} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          {page.blocks.length === 0 && (
            <p className="text-xs italic mt-2" style={{ color: "var(--mv-text-muted)" }}>Komponenten hinzufügen, um sie hier zu sortieren.</p>
          )}
        </div>
      </aside>

      {/* Center: iframe preview */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="border-b px-4 py-2.5 flex items-center justify-between flex-shrink-0" style={{ background: "#fff", borderColor: "var(--mv-border)" }}>
          <div className="flex items-center gap-1 p-0.5 rounded-full border" style={{ borderColor: "var(--mv-border)" }}>
            {Object.entries(DEVICES).map(([k, d]) => {
              const Ico = d.icon;
              const active = device === k;
              return (
                <button
                  key={k}
                  data-testid={`device-${k}`}
                  onClick={() => setDevice(k)}
                  className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 transition-colors ${active ? "bg-[var(--mv-text)] text-white" : "text-[var(--mv-text-muted)] hover:bg-[var(--mv-surface)]"}`}
                >
                  <Ico className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {d.label}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs flex items-center gap-1.5" style={{ color: "var(--mv-text-muted)" }}>
              <input type="checkbox" data-testid="page-published-toggle" checked={!!page.published} onChange={(e) => update({ published: e.target.checked })} />
              Veröffentlicht
            </label>
            <label className="text-xs flex items-center gap-1.5 mr-2" style={{ color: "var(--mv-text-muted)" }}>
              <input type="checkbox" data-testid="page-home-toggle" checked={!!page.is_home} onChange={(e) => update({ is_home: e.target.checked })} />
              Startseite
            </label>
            {salon && (
              <a href={`/salon/${salon.slug}/${page.is_home ? "" : page.slug}`} target="_blank" rel="noreferrer" className="mv-btn-ghost text-xs" data-testid="open-live-btn">
                <Eye className="w-3.5 h-3.5" strokeWidth={1.5} /> Live
              </a>
            )}
            <button data-testid="save-page-btn" onClick={save} disabled={saving} className="mv-btn text-sm">
              <Save className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Speichert..." : "Speichern"}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-4 flex justify-center" onDragOver={onCanvasDragOver} onDrop={onCanvasDrop} data-testid="canvas-drop-zone">
          <div
            className="bg-white shadow-2xl rounded-xl overflow-hidden transition-all duration-300"
            style={{ width: DEVICES[device].width, maxWidth: "100%", height: "100%" }}
          >
            <iframe
              ref={iframeRef}
              title="Vorschau"
              src={previewUrl}
              onLoad={onIframeLoad}
              className="w-full h-full border-0"
              data-testid="builder-iframe"
            />
          </div>
        </div>
      </div>

      {/* Right: Properties */}
      <aside className="w-72 border-l overflow-y-auto flex-shrink-0" style={{ background: "#fff", borderColor: "var(--mv-border)" }}>
        <div className="p-4 border-b" style={{ borderColor: "var(--mv-border)" }}>
          <p className="mv-label">Eigenschaften</p>
        </div>
        {!selectedBlock ? (
          <div className="p-5 text-sm" style={{ color: "var(--mv-text-muted)" }}>
            Wählen Sie einen Block aus, um Eigenschaften zu bearbeiten.
            <div className="mt-6 p-4 rounded-lg" style={{ background: "var(--mv-surface)" }}>
              <p className="mv-label mb-2">Seite</p>
              <label className="block text-xs mb-1">URL Slug</label>
              <input className="mv-input mb-3 text-sm" value={page.slug} onChange={(e) => update({ slug: e.target.value })} />
              <label className="block text-xs mb-1">SEO Titel</label>
              <input data-testid="page-seo-title" className="mv-input mb-3 text-sm" value={page.seo?.title || ""} onChange={(e) => update({ seo: { ...page.seo, title: e.target.value } })} />
              <label className="block text-xs mb-1">Meta-Beschreibung</label>
              <textarea data-testid="page-seo-description" className="mv-input text-sm min-h-[80px] mb-3" value={page.seo?.description || ""} onChange={(e) => update({ seo: { ...page.seo, description: e.target.value } })} />
              <label className="block text-xs mb-1">Keywords (komma­getrennt)</label>
              <input data-testid="page-seo-keywords" className="mv-input text-sm" placeholder="z.B. Friseur Berlin, Haarschnitt, Färben" value={page.seo?.keywords || ""} onChange={(e) => update({ seo: { ...page.seo, keywords: e.target.value } })} />
            </div>
          </div>
        ) : (
          <PropsPanel block={selectedBlock} onChange={(p) => updateBlockProps(selectedBlock.id, p)} onRemove={() => removeBlock(selectedBlock.id)} forms={forms} services={services} staff={staff} categories={categories} />
        )}
      </aside>
      <TemplatePicker open={templateOpen} onClose={() => setTemplateOpen(false)} onInsert={insertTemplate} />
    </div>
  );
}

function SortableOutlineItem({ block, selected, onSelect, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const Ico = ICONS[block.type] || Layers;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`outline-${block.type}-${block.id}`}
      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs transition-colors ${selected ? "bg-[var(--mv-primary)] text-white" : "hover:bg-[var(--mv-surface)]"}`}
    >
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
        <GripVertical className="w-3 h-3 opacity-60" strokeWidth={1.5} />
      </button>
      <button onClick={onSelect} className="flex-1 flex items-center gap-1.5 text-left">
        <Ico className="w-3 h-3" strokeWidth={1.5} />
        <span className="truncate">{block.type}</span>
      </button>
      <button onClick={onRemove} className="opacity-50 hover:opacity-100">
        <Trash2 className="w-3 h-3" strokeWidth={1.5} />
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-4">
      <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--mv-text-muted)" }}>{label}</label>
      {children}
    </div>
  );
}

function PropsPanel({ block, onChange, onRemove, forms, services, staff, categories = [] }) {
  const { type, props } = block;
  return (
    <div className="p-5">
      <div className="flex justify-between items-center mb-4">
        <p className="mv-label">{type}</p>
        <button onClick={onRemove} className="text-xs text-red-500 flex items-center gap-1"><Trash2 className="w-3 h-3" /> Entfernen</button>
      </div>
      {type === "heading" && (
        <>
          <Field label="Text"><input className="mv-input text-sm" value={props.text} onChange={(e) => onChange({ text: e.target.value })} /></Field>
          <Field label="Ebene">
            <select className="mv-input text-sm" value={props.level} onChange={(e) => onChange({ level: e.target.value })}>
              {["h1", "h2", "h3", "h4"].map((l) => <option key={l}>{l}</option>)}
            </select>
          </Field>
          <Field label="Ausrichtung">
            <select className="mv-input text-sm" value={props.align} onChange={(e) => onChange({ align: e.target.value })}>
              <option value="left">Links</option><option value="center">Mitte</option><option value="right">Rechts</option>
            </select>
          </Field>
        </>
      )}
      {type === "text" && (
        <>
          <Field label="Inhalt">
            <RichTextEditor
              value={props.text}
              onChange={(v) => onChange({ text: v })}
              placeholder="**fett**, *kursiv*, [link](url), Listen, …"
              minHeight="160px"
              testId="cms-text-rte"
            />
          </Field>
          <Field label="Ausrichtung">
            <select className="mv-input text-sm" value={props.align} onChange={(e) => onChange({ align: e.target.value })}>
              <option value="left">Links</option><option value="center">Mitte</option><option value="right">Rechts</option>
            </select>
          </Field>
        </>
      )}
      {type === "image" && (
        <>
          <Field label="Bild"><ImageUploadField value={props.src} onChange={(v) => onChange({ src: v })} /></Field>
          <Field label="Alt-Text"><input className="mv-input text-sm" value={props.alt || ""} onChange={(e) => onChange({ alt: e.target.value })} /></Field>
          <Field label="Bildunterschrift"><input className="mv-input text-sm" value={props.caption || ""} onChange={(e) => onChange({ caption: e.target.value })} /></Field>
        </>
      )}
      {type === "image_fullwidth" && (
        <>
          <Field label="Bild"><ImageUploadField value={props.src} onChange={(v) => onChange({ src: v })} /></Field>
          <Field label="Alt-Text"><input className="mv-input text-sm" value={props.alt || ""} onChange={(e) => onChange({ alt: e.target.value })} /></Field>
          <Field label={`Höhe (${props.height || 480}px)`}>
            <input
              type="range" min="200" max="900" step="20"
              data-testid="img-fw-height"
              value={props.height || 480}
              onChange={(e) => onChange({ height: parseInt(e.target.value, 10) })}
              className="w-full"
            />
          </Field>
          <Field label="Overlay-Titel (optional)"><input className="mv-input text-sm" value={props.overlay_title || ""} onChange={(e) => onChange({ overlay_title: e.target.value })} /></Field>
          <Field label="Overlay-Untertitel (optional)"><input className="mv-input text-sm" value={props.overlay_subtitle || ""} onChange={(e) => onChange({ overlay_subtitle: e.target.value })} /></Field>
          {(props.overlay_title || props.overlay_subtitle) && (
            <>
              <Field label="Text-Ausrichtung">
                <div className="grid grid-cols-3 gap-1" data-testid="img-fw-align">
                  {[
                    { value: "left", label: "Links" },
                    { value: "center", label: "Mitte" },
                    { value: "right", label: "Rechts" },
                  ].map((opt) => {
                    const active = (props.text_align || "center") === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        data-testid={`img-fw-align-${opt.value}`}
                        onClick={() => onChange({ text_align: opt.value })}
                        className="text-center p-1.5 rounded border text-xs"
                        style={{
                          borderColor: active ? "var(--mv-primary)" : "var(--mv-border)",
                          background: active ? "rgba(20,184,166,0.06)" : "transparent",
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </Field>
              <Field label={`Overlay-Dunkelheit (${Math.round((typeof props.overlay_opacity === "number" ? props.overlay_opacity : 0.35) * 100)}%)`}>
                <input
                  type="range" min="0" max="0.9" step="0.05"
                  data-testid="img-fw-overlay-opacity"
                  value={typeof props.overlay_opacity === "number" ? props.overlay_opacity : 0.35}
                  onChange={(e) => onChange({ overlay_opacity: parseFloat(e.target.value) })}
                  className="w-full"
                />
              </Field>
            </>
          )}
        </>
      )}
      {type === "button" && (
        <>
          <Field label="Label"><input className="mv-input text-sm" value={props.label} onChange={(e) => onChange({ label: e.target.value })} /></Field>
          <Field label="URL"><input className="mv-input text-sm" value={props.url} onChange={(e) => onChange({ url: e.target.value })} /></Field>
        </>
      )}
      {type === "hero" && (
        <>
          <Field label="Titel"><input className="mv-input text-sm" value={props.title} onChange={(e) => onChange({ title: e.target.value })} /></Field>
          <Field label="Untertitel"><input className="mv-input text-sm" value={props.subtitle} onChange={(e) => onChange({ subtitle: e.target.value })} /></Field>
          <Field label="CTA Label"><input className="mv-input text-sm" value={props.cta_label} onChange={(e) => onChange({ cta_label: e.target.value })} /></Field>
          <Field label="CTA URL"><input className="mv-input text-sm" value={props.cta_url} onChange={(e) => onChange({ cta_url: e.target.value })} /></Field>
          <Field label="Hero-Layout">
            <div className="grid grid-cols-1 gap-1.5" data-testid="cms-hero-layout-picker">
              {[
                { value: "split", label: "Bild rechts", hint: "Text links, Bild als Card rechts" },
                { value: "background", label: "Bild im Hintergrund", hint: "Bild als Banner-Hintergrund mit Overlay" },
                { value: "fullscreen", label: "Full-Window (100vh)", hint: "Bild füllt gesamten Viewport" },
              ].map((opt) => {
                const active = (props.layout || "background") === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    data-testid={`cms-hero-layout-${opt.value}`}
                    onClick={() => onChange({ layout: opt.value })}
                    className="text-left p-2 rounded border transition-all"
                    style={{
                      borderColor: active ? "var(--mv-primary)" : "var(--mv-border)",
                      background: active ? "rgba(20,184,166,0.06)" : "transparent",
                    }}
                  >
                    <div className="text-xs font-medium">{opt.label}</div>
                    <div className="text-[10px] opacity-60 mt-0.5">{opt.hint}</div>
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Hintergrundbild"><ImageUploadField value={props.image} onChange={(v) => onChange({ image: v })} /></Field>
          {(props.layout || "background") !== "split" && (
            <Field label={`Overlay-Dunkelheit (${Math.round((typeof props.overlay_opacity === "number" ? props.overlay_opacity : 0.35) * 100)}%)`}>
              <input
                type="range" min="0" max="0.9" step="0.05"
                data-testid="cms-hero-overlay-opacity"
                value={typeof props.overlay_opacity === "number" ? props.overlay_opacity : 0.35}
                onChange={(e) => onChange({ overlay_opacity: parseFloat(e.target.value) })}
                className="w-full"
              />
            </Field>
          )}
        </>
      )}
      {type === "slider" && (
        <Field label="Bilder">
          <div className="space-y-2">
            {(props.images || []).map((src, i) => (
              <div key={i} className="flex gap-1">
                <input className="mv-input text-xs flex-1" value={src} onChange={(e) => {
                  const arr = [...props.images]; arr[i] = e.target.value; onChange({ images: arr });
                }} />
                <button onClick={() => onChange({ images: props.images.filter((_, j) => j !== i) })} className="p-2 hover:bg-red-50 rounded"><Trash2 className="w-3 h-3 text-red-500" /></button>
              </div>
            ))}
            <button onClick={() => onChange({ images: [...(props.images || []), ""] })} className="mv-btn-ghost text-xs w-full"><Plus className="w-3 h-3" /> Bild hinzufügen</button>
          </div>
        </Field>
      )}
      {type === "testimonials" && (
        <Field label="Einträge">
          <div className="space-y-3">
            {(props.items || []).map((it, i) => (
              <div key={i} className="p-3 rounded border" style={{ borderColor: "var(--mv-border)" }}>
                <textarea className="mv-input text-xs mb-1" value={it.quote} onChange={(e) => {
                  const arr = [...props.items]; arr[i] = { ...it, quote: e.target.value }; onChange({ items: arr });
                }} />
                <input className="mv-input text-xs" placeholder="Autor" value={it.author} onChange={(e) => {
                  const arr = [...props.items]; arr[i] = { ...it, author: e.target.value }; onChange({ items: arr });
                }} />
                <button onClick={() => onChange({ items: props.items.filter((_, j) => j !== i) })} className="text-xs text-red-500 mt-1">Entfernen</button>
              </div>
            ))}
            <button onClick={() => onChange({ items: [...(props.items || []), { quote: "", author: "" }] })} className="mv-btn-ghost text-xs w-full"><Plus className="w-3 h-3" /> Hinzufügen</button>
          </div>
        </Field>
      )}
      {type === "faq" && (
        <>
          <Field label="Spalten">
            <div className="grid grid-cols-2 gap-2" data-testid="faq-columns-picker">
              {[
                { value: 1, label: "1 Spalte" },
                { value: 2, label: "2 Spalten" },
              ].map((opt) => {
                const active = (props.columns || 1) === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    data-testid={`faq-columns-${opt.value}`}
                    onClick={() => onChange({ columns: opt.value })}
                    className="text-center p-2 rounded border text-xs"
                    style={{
                      borderColor: active ? "var(--mv-primary)" : "var(--mv-border)",
                      background: active ? "rgba(20,184,166,0.06)" : "transparent",
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Fragen">
            <div className="space-y-3">
              {(props.items || []).map((it, i) => (
                <div key={i} className="p-3 rounded border" style={{ borderColor: "var(--mv-border)" }}>
                  <input className="mv-input text-xs mb-1" placeholder="Frage" value={it.question} onChange={(e) => {
                    const arr = [...props.items]; arr[i] = { ...it, question: e.target.value }; onChange({ items: arr });
                  }} />
                  <textarea className="mv-input text-xs" placeholder="Antwort" value={it.answer} onChange={(e) => {
                    const arr = [...props.items]; arr[i] = { ...it, answer: e.target.value }; onChange({ items: arr });
                  }} />
                  <button onClick={() => onChange({ items: props.items.filter((_, j) => j !== i) })} className="text-xs text-red-500 mt-1">Entfernen</button>
                </div>
              ))}
              <button onClick={() => onChange({ items: [...(props.items || []), { question: "", answer: "" }] })} className="mv-btn-ghost text-xs w-full"><Plus className="w-3 h-3" /> Hinzufügen</button>
            </div>
          </Field>
        </>
      )}
      {type === "spacer" && (
        <Field label="Höhe (px)"><input type="number" className="mv-input text-sm" value={props.height} onChange={(e) => onChange({ height: parseInt(e.target.value) || 0 })} /></Field>
      )}
      {type === "form" && (
        <Field label="Formular">
          <select className="mv-input text-sm" value={props.form_id || ""} onChange={(e) => onChange({ form_id: e.target.value })}>
            <option value="">— Wählen —</option>
            {forms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </Field>
      )}
      {(type === "booking" || type === "newsletter") && (
        <>
          <Field label="Titel"><input className="mv-input text-sm" value={props.title || ""} onChange={(e) => onChange({ title: e.target.value })} /></Field>
          <Field label="Untertitel"><input className="mv-input text-sm" value={props.subtitle || ""} onChange={(e) => onChange({ subtitle: e.target.value })} /></Field>
          {type === "booking" && (
            <div className="text-xs p-3 rounded-lg" style={{ background: "var(--mv-surface)", color: "var(--mv-text-muted)" }}>
              {services.length} aktive Leistungen werden im Dropdown angezeigt. Verwaltung: <Link to="/cms/services" className="underline">Leistungen</Link>
            </div>
          )}
        </>
      )}
      {type === "team" && (
        <>
          <Field label="Titel"><input className="mv-input text-sm" value={props.title || ""} onChange={(e) => onChange({ title: e.target.value })} /></Field>
          <Field label="Untertitel"><input className="mv-input text-sm" value={props.subtitle || ""} onChange={(e) => onChange({ subtitle: e.target.value })} /></Field>
          <Field label="Spalten">
            <select className="mv-input text-sm" value={props.columns || 3} onChange={(e) => onChange({ columns: parseInt(e.target.value) })}>
              <option value={2}>2 Spalten</option>
              <option value={3}>3 Spalten</option>
              <option value={4}>4 Spalten</option>
            </select>
          </Field>
          <Field label="Karten-Stil">
            <select className="mv-input text-sm" value={props.card_style || "elevated"} onChange={(e) => onChange({ card_style: e.target.value })}>
              <option value="elevated">Card mit Foto oben</option>
              <option value="overlay">Foto mit Overlay-Text</option>
            </select>
          </Field>
          <Field label="Anzeige">
            <label className="flex items-center gap-2 text-xs mb-1.5"><input type="checkbox" checked={props.show_bio !== false} onChange={(e) => onChange({ show_bio: e.target.checked })} /> Bio anzeigen</label>
            <label className="flex items-center gap-2 text-xs mb-1.5"><input type="checkbox" checked={props.show_contact !== false} onChange={(e) => onChange({ show_contact: e.target.checked })} /> Telefon & E-Mail anzeigen</label>
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={props.show_social !== false} onChange={(e) => onChange({ show_social: e.target.checked })} /> Social Media anzeigen</label>
          </Field>
          <Field label="Mitglieder">
            {(staff || []).length === 0 ? (
              <p className="text-xs italic" style={{ color: "var(--mv-text-muted)" }}>Noch keine Team-Mitglieder. Verwalte sie unter <Link to="/cms/staff" className="underline">Team</Link>.</p>
            ) : (
              <>
                <p className="text-[11px] mb-2" style={{ color: "var(--mv-text-muted)" }}>Leer lassen = alle aktiven Mitglieder werden angezeigt.</p>
                <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
                  {(staff || []).filter((s) => s.active !== false).map((s) => {
                    const ids = props.staff_ids || [];
                    const sel = ids.includes(s.id);
                    return (
                      <label key={s.id} className="flex items-center gap-2 text-xs p-1.5 rounded cursor-pointer hover:bg-[var(--mv-surface)]" data-testid={`team-staff-pick-${s.id}`}>
                        <input type="checkbox" checked={sel} onChange={(e) => onChange({ staff_ids: e.target.checked ? [...ids, s.id] : ids.filter((x) => x !== s.id) })} />
                        {s.photo_url && <img src={s.photo_url} alt="" className="w-5 h-5 rounded-full object-cover" />}
                        <span className="truncate">{s.name}</span>
                        <span className="opacity-60 text-[10px]">{s.role}</span>
                      </label>
                    );
                  })}
                </div>
                {(props.staff_ids || []).length > 0 && (
                  <button onClick={() => onChange({ staff_ids: [] })} className="text-[11px] underline mt-2" style={{ color: "var(--mv-text-muted)" }}>Auswahl zurücksetzen</button>
                )}
              </>
            )}
          </Field>
        </>
      )}
      {type === "loyalty" && (
        <>
          <Field label="Titel"><input className="mv-input text-sm" value={props.title || ""} onChange={(e) => onChange({ title: e.target.value })} /></Field>
          <Field label="Untertitel"><textarea className="mv-input text-sm min-h-[60px]" value={props.subtitle || ""} onChange={(e) => onChange({ subtitle: e.target.value })} /></Field>
          <Field label="CTA Beschriftung"><input className="mv-input text-sm" value={props.cta_label || ""} onChange={(e) => onChange({ cta_label: e.target.value })} /></Field>
          <Field label="Anzahl angezeigter Belohnungen"><input type="number" min="1" max="6" className="mv-input text-sm" value={props.max_rewards || 3} onChange={(e) => onChange({ max_rewards: parseInt(e.target.value) || 3 })} /></Field>
          <div className="text-xs p-3 rounded-lg" style={{ background: "var(--mv-surface)", color: "var(--mv-text-muted)" }}>
            Belohnungen + Punkte-Regeln werden unter <Link to="/cms/loyalty" className="underline">Treueprogramm</Link> verwaltet.
          </div>
        </>
      )}
      {type === "pricelist" && (
        <>
          <Field label="Titel">
            <input data-testid="pricelist-title" className="mv-input text-sm" value={props.title || ""} onChange={(e) => onChange({ title: e.target.value })} />
          </Field>
          <Field label="Untertitel (optional)">
            <input data-testid="pricelist-subtitle" className="mv-input text-sm" value={props.subtitle || ""} onChange={(e) => onChange({ subtitle: e.target.value })} />
          </Field>
          <Field label="Darstellung">
            <div className="grid grid-cols-2 gap-2" data-testid="pricelist-layout-picker">
              {[
                { value: "list", label: "Liste" },
                { value: "cards", label: "Karten" },
              ].map((opt) => {
                const active = (props.layout || "list") === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    data-testid={`pricelist-layout-${opt.value}`}
                    onClick={() => onChange({ layout: opt.value })}
                    className="text-center p-2 rounded border text-xs"
                    style={{
                      borderColor: active ? "var(--mv-primary)" : "var(--mv-border)",
                      background: active ? "rgba(20,184,166,0.06)" : "transparent",
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Anzeigeoptionen">
            <label className="flex items-center gap-2 text-xs mb-1.5">
              <input data-testid="pricelist-group-toggle" type="checkbox" checked={props.group_by_category !== false} onChange={(e) => onChange({ group_by_category: e.target.checked })} />
              Nach Kategorien gruppieren
            </label>
            <label className="flex items-center gap-2 text-xs mb-1.5">
              <input data-testid="pricelist-uncategorized-toggle" type="checkbox" checked={props.show_uncategorized !== false} onChange={(e) => onChange({ show_uncategorized: e.target.checked })} disabled={props.group_by_category === false} />
              Unkategorisierte als &bdquo;Weitere Leistungen&ldquo;
            </label>
            <label className="flex items-center gap-2 text-xs mb-1.5">
              <input data-testid="pricelist-description-toggle" type="checkbox" checked={props.show_description !== false} onChange={(e) => onChange({ show_description: e.target.checked })} />
              Beschreibung anzeigen
            </label>
            <label className="flex items-center gap-2 text-xs mb-1.5">
              <input data-testid="pricelist-duration-toggle" type="checkbox" checked={props.show_duration !== false} onChange={(e) => onChange({ show_duration: e.target.checked })} />
              Dauer (Minuten) anzeigen
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input data-testid="pricelist-addons-toggle" type="checkbox" checked={props.show_addons !== false} onChange={(e) => onChange({ show_addons: e.target.checked })} />
              Zusatzleistungen anzeigen
            </label>
          </Field>
          <Field label="Kategorien filtern">
            {(categories || []).length === 0 ? (
              <p className="text-xs italic" style={{ color: "var(--mv-text-muted)" }}>
                Keine Kategorien angelegt. Pflege sie unter <Link to="/cms/services" className="underline">Leistungen</Link>.
              </p>
            ) : (
              <>
                <p className="text-[11px] mb-2" style={{ color: "var(--mv-text-muted)" }}>Leer lassen = alle Kategorien anzeigen.</p>
                <div className="space-y-1 max-h-44 overflow-y-auto pr-1">
                  {categories.filter((c) => c.active !== false).map((c) => {
                    const ids = props.category_ids || [];
                    const sel = ids.includes(c.id);
                    return (
                      <label key={c.id} className="flex items-center gap-2 text-xs p-1.5 rounded cursor-pointer hover:bg-[var(--mv-surface)]" data-testid={`pricelist-cat-pick-${c.id}`}>
                        <input type="checkbox" checked={sel} onChange={(e) => onChange({ category_ids: e.target.checked ? [...ids, c.id] : ids.filter((x) => x !== c.id) })} />
                        <span className="truncate">{c.name}</span>
                      </label>
                    );
                  })}
                </div>
                {(props.category_ids || []).length > 0 && (
                  <button onClick={() => onChange({ category_ids: [] })} className="text-[11px] underline mt-2" style={{ color: "var(--mv-text-muted)" }}>Auswahl zurücksetzen</button>
                )}
              </>
            )}
          </Field>
          <Field label="Buchungs-CTA">
            <label className="flex items-center gap-2 text-xs mb-2">
              <input data-testid="pricelist-cta-toggle" type="checkbox" checked={props.show_cta !== false} onChange={(e) => onChange({ show_cta: e.target.checked })} />
              CTA-Button am Ende anzeigen
            </label>
            <input
              data-testid="pricelist-cta-label"
              className="mv-input text-sm"
              placeholder="Termin online buchen"
              value={props.cta_label || ""}
              onChange={(e) => onChange({ cta_label: e.target.value })}
              disabled={props.show_cta === false}
            />
          </Field>
          <div className="text-xs p-3 rounded-lg" style={{ background: "var(--mv-surface)", color: "var(--mv-text-muted)" }}>
            {services.length} aktive Leistungen werden automatisch aus <Link to="/cms/services" className="underline">Leistungen</Link> übernommen.
          </div>
        </>
      )}
    </div>
  );
}

function ImageUploadField({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/cms/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
    } finally { setBusy(false); }
  };
  return (
    <div>
      <input className="mv-input text-xs mb-2" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="URL oder hochladen" />
      <label className="mv-btn-ghost text-xs cursor-pointer w-full justify-center">
        <Upload className="w-3 h-3" /> {busy ? "Lädt..." : "Hochladen"}
        <input type="file" accept="image/*" className="hidden" onChange={upload} />
      </label>
    </div>
  );
}
